/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PageContainer from "../page-container/page-container";
import LoadingScreen from "../progress/loading-screen";
import { useNotification } from "../../context/notification-provider";
import useClients from "../client-config/data/use-clients";
import useMdaSpreadsheetMappings from "./use-mda-spreadsheet-mappings";
import { reorderMappings, updateMapping } from "./service";
import { DEFAULT_MAPPING_CLIENT_ID, MdaExtractMappingDetail } from "./types";
import MappingList from "./mapping-list";
import MappingCreateDialog from "./mapping-create-dialog";
import MappingEditDialog from "./mapping-edit-dialog";
import MappingDeleteDialog from "./mapping-delete-dialog";

interface ClientOption {
  id: number;
  name: string;
}

const DEFAULT_CLIENT_OPTION: ClientOption = {
  id: DEFAULT_MAPPING_CLIENT_ID,
  name: "Default (all clients)",
};

const MdaSpreadsheetMappings = () => {
  const { showNotification } = useNotification();
  const {
    data: clients,
    isLoading: isLoadingClients,
    error: clientsError,
  } = useClients();

  const [selectedClientId, setSelectedClientId] = React.useState<number>(
    DEFAULT_MAPPING_CLIENT_ID,
  );

  const {
    data: mappings,
    isLoading: isLoadingMappings,
    error: mappingsError,
    mutate,
  } = useMdaSpreadsheetMappings(selectedClientId);

  const [dialog, setDialog] = React.useState<"create" | "edit" | "delete" | "">(
    "",
  );
  const [selectedMapping, setSelectedMapping] =
    React.useState<MdaExtractMappingDetail | null>(null);

  const sortedMappings = React.useMemo(
    () =>
      [...(mappings ?? [])].sort((a, b) => a.columnPosition - b.columnPosition),
    [mappings],
  );

  const clientOptions = React.useMemo<ClientOption[]>(
    () => [
      DEFAULT_CLIENT_OPTION,
      ...(clients ?? [])
        .filter((client) => !client.deleted)
        .map((client) => ({ id: client.id, name: client.name })),
    ],
    [clients],
  );

  const selectedClientOption = React.useMemo(
    () =>
      clientOptions.find((option) => option.id === selectedClientId) ??
      DEFAULT_CLIENT_OPTION,
    [clientOptions, selectedClientId],
  );

  const handleClientChange = React.useCallback(
    (_event: React.SyntheticEvent, newValue: ClientOption | null) => {
      setSelectedClientId(newValue?.id ?? DEFAULT_MAPPING_CLIENT_ID);
    },
    [],
  );

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: "Refreshing column mappings...",
      severity: "info",
      duration: 2000,
    });
    mutate();
  }, [showNotification, mutate]);

  const handleCreateClick = React.useCallback(() => {
    setDialog("create");
  }, []);

  const handleOnDialogClose = React.useCallback(() => {
    setDialog("");
    setSelectedMapping(null);
    mutate();
  }, [mutate]);

  const handleOnEdit = React.useCallback((mapping: MdaExtractMappingDetail) => {
    setSelectedMapping(mapping);
    setDialog("edit");
  }, []);

  const handleOnDelete = React.useCallback(
    (mapping: MdaExtractMappingDetail) => {
      setSelectedMapping(mapping);
      setDialog("delete");
    },
    [],
  );

  const handleToggleEnabled = React.useCallback(
    async (mapping: MdaExtractMappingDetail) => {
      try {
        await updateMapping({
          id: mapping.id,
          sourceColumn: mapping.sourceColumn,
          destinationColumn: mapping.destinationColumn,
          columnPosition: mapping.columnPosition,
          enabled: !mapping.enabled,
        });
        mutate();
      } catch (err) {
        showNotification({
          message: `Failed to update column mapping. Reason: ${(err as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      }
    },
    [mutate, showNotification],
  );

  const handleReorder = React.useCallback(
    async (orderedMappings: MdaExtractMappingDetail[]) => {
      const optimisticMappings = orderedMappings.map((mapping, index) => ({
        ...mapping,
        columnPosition: index,
      }));
      const positions = optimisticMappings.map((mapping) => ({
        id: mapping.id,
        columnPosition: mapping.columnPosition,
      }));

      mutate(optimisticMappings, { revalidate: false });

      try {
        await reorderMappings(selectedClientId, positions);
        mutate();
      } catch (err) {
        showNotification({
          message: `Failed to reorder column mappings. Reason: ${(err as Error).message}`,
          severity: "error",
          duration: 3000,
        });
        mutate();
      }
    },
    [selectedClientId, mutate, showNotification],
  );

  if (isLoadingClients) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer
      title="MDA Spreadsheet Column Mappings"
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="left" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleOnRefreshClick}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Add Mapping
          </Button>
        </Stack>
      }
    >
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          id="mapping-client-select"
          size="small"
          fullWidth
          disableClearable
          options={clientOptions}
          value={selectedClientOption}
          onChange={handleClientChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No matching clients"
          ListboxProps={{ style: { maxHeight: 500 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              placeholder="Search clients"
            />
          )}
        />
      </Box>

      {clientsError || mappingsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading column mappings.
        </Alert>
      ) : (
        <Paper elevation={2} sx={{ p: 2 }}>
          {isLoadingMappings ? (
            <LoadingScreen />
          ) : (
            <MappingList
              mappings={sortedMappings}
              onReorder={handleReorder}
              onToggleEnabled={handleToggleEnabled}
              onEdit={handleOnEdit}
              onDelete={handleOnDelete}
            />
          )}
        </Paper>
      )}

      {dialog === "create" && (
        <MappingCreateDialog
          clientId={selectedClientId}
          nextColumnPosition={sortedMappings.length}
          open={dialog === "create"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "edit" && selectedMapping && (
        <MappingEditDialog
          selectedMapping={selectedMapping}
          open={dialog === "edit"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "delete" && selectedMapping && (
        <MappingDeleteDialog
          selectedMapping={selectedMapping}
          open={dialog === "delete"}
          onClose={handleOnDialogClose}
        />
      )}
    </PageContainer>
  );
};

export default MdaSpreadsheetMappings;
